// services/authApi.js — Auth API client for Stock Monitor
// Communicates with Worker auth endpoints.

import { API_BASE } from '../config';

async function authFetch(path, options = {}) {
  const token = sessionStorage.getItem('sm_session_token');
  const headers = {
    'Content-Type': 'application/json',
    'X-Requested-With': 'XMLHttpRequest',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...options.headers,
  };
  const res = await fetch(API_BASE + path, { ...options, headers });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || `Auth error ${res.status}`);
  return data;
}

// ── Registration ────────────────────────────────────────────

export async function register(email, password, registrationCode) {
  return authFetch('/auth/register', {
    method: 'POST',
    body: JSON.stringify({ email, password, registrationCode }),
  });
}

export async function verifyTotp(userId, code) {
  return authFetch('/auth/verify-totp', {
    method: 'POST',
    body: JSON.stringify({ userId, code }),
  });
}

// ── Login (2-step) ──────────────────────────────────────────

export async function login(email, password) {
  return authFetch('/auth/login', {
    method: 'POST',
    body: JSON.stringify({ email, password }),
  });
}

export async function login2FA(preAuthToken, code) {
  const data = await authFetch('/auth/login/2fa', {
    method: 'POST',
    body: JSON.stringify({ preAuthToken, code }),
  });
  // Store session token
  if (data.token) {
    sessionStorage.setItem('sm_session_token', data.token);
    sessionStorage.setItem('sm_user', JSON.stringify(data.user));
  }
  return data;
}

// ── Session ─────────────────────────────────────────────────

export async function getMe() {
  return authFetch('/auth/me', { method: 'GET' });
}

export async function logout() {
  try {
    await authFetch('/auth/logout', { method: 'POST' });
  } catch {
    // Ignore errors on logout (token may already be expired)
  } finally {
    sessionStorage.removeItem('sm_session_token');
    sessionStorage.removeItem('sm_user');
  }
}

// ── Password & Session management ───────────────────────────

export async function changePassword(currentPassword, newPassword, totpCode) {
  const data = await authFetch('/auth/change-password', {
    method: 'POST',
    body: JSON.stringify({ currentPassword, newPassword, totpCode }),
  });
  // Server returns a new session token (old sessions revoked)
  if (data.token) {
    sessionStorage.setItem('sm_session_token', data.token);
  }
  return data;
}

export async function revokeAllSessions() {
  const data = await authFetch('/auth/sessions/revoke-all', {
    method: 'POST',
  });
  if (data.token) {
    sessionStorage.setItem('sm_session_token', data.token);
  }
  return data;
}

// ── Admin endpoints ─────────────────────────────────────────

export async function adminListUsers() {
  return authFetch('/admin/users', { method: 'GET' });
}

export async function adminDeleteUser(userId) {
  return authFetch(`/admin/users/${userId}`, { method: 'DELETE' });
}

export async function adminResetTotp(userId) {
  return authFetch(`/admin/users/${userId}/reset-2fa`, { method: 'POST' });
}

export async function adminRevokeSessions(userId) {
  return authFetch(`/admin/users/${userId}/revoke-sessions`, { method: 'POST' });
}

// ── Helpers ─────────────────────────────────────────────────

export function getStoredUser() {
  try {
    const raw = sessionStorage.getItem('sm_user');
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function getSessionToken() {
  return sessionStorage.getItem('sm_session_token');
}

export function isAuthenticated() {
  return !!sessionStorage.getItem('sm_session_token');
}
